import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RefreshCw, Play, Trophy, Clock, User, XCircle, Volume2, VolumeX } from 'lucide-react';

const initialBeadCirclesData = [
  { id: 1, num: 2, color: '#79b379', name: 'Green Circle' },
  { id: 2, num: 7, color: '#cd807f', name: 'Pink Circle' },
  { id: 3, num: 6, color: '#49b9e3', name: 'Blue Circle' },
  { id: 4, num: 5, color: '#edde81', name: 'Yellow Circle' },
  { id: 5, num: 9, color: '#eb80af', name: 'Magenta Circle' },
];

const TOTAL_BEADS_PER_CIRCLE = 10;
const BEAD_COLOR = '#48c6ef'; // Active bead highlight color

export default function CountAndColorBeadsGame() {
  const [playerName, setPlayerName] = useState('');
  const [activePlayer, setActivePlayer] = useState('Little Learner');
  const [isMuted, setIsMuted] = useState(false);

  // Game state: Track selected bead indices per circle ID { [circleId]: number[] }
  const [selectedBeads, setSelectedBeads] = useState(() => ({
    1: [], 2: [], 3: [], 4: [], 5: []
  }));

  const [timer, setTimer] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [gameResult, setGameResult] = useState({ ok: false, message: '' });

  // Web Speech API Voice synthesis helper
  const speakText = (text) => {
    if (isMuted) return;
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.1;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Keep activePlayer in sync when input changes directly
  const handleNameInputChange = (e) => {
    const value = e.target.value;
    setPlayerName(value);
    setActivePlayer(value.trim() ? value.trim() : 'Little Learner');
  };

  // Toggle voice mute state
  const toggleMute = () => {
    if (!isMuted && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsMuted(!isMuted);
  };

  // Announce instructions on initial render
  useEffect(() => {
    speakText("Welcome! Please enter your name, then click beads to color them according to the given number.");
  }, []);

  // Timer Effect
  useEffect(() => {
    let interval = null;
    if (isTimerActive) {
      interval = setInterval(() => {
        setTimer((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerActive]);

  // Toggle bead state (color/uncolor)
  const handleBeadClick = (circleId, beadIndex) => {
    setSelectedBeads((prev) => {
      const currentBeads = prev[circleId] || [];
      const exists = currentBeads.includes(beadIndex);
      const updatedBeads = exists
        ? currentBeads.filter((i) => i !== beadIndex)
        : [...currentBeads, beadIndex];

      return { ...prev, [circleId]: updatedBeads };
    });
  };

  const handleResetCurrent = () => {
    setSelectedBeads({ 1: [], 2: [], 3: [], 4: [], 5: [] });
    setTimer(0);
    setIsTimerActive(true);
    speakText("Beads cleared. Try again!");
  };

  const handlePlayAgain = () => {
    const currentName = playerName.trim() || 'Little Learner';
    setActivePlayer(currentName);

    // Reset game state
    setSelectedBeads({ 1: [], 2: [], 3: [], 4: [], 5: [] });
    setTimer(0);
    setIsTimerActive(true);

    speakText(`Welcome ${currentName}! Count and color the beads according to the target number.`);
  };

  const handleSubmit = () => {
    setIsTimerActive(false);

    let mistakes = 0;
    initialBeadCirclesData.forEach((circle) => {
      const count = (selectedBeads[circle.id] || []).length;
      if (count !== circle.num) {
        mistakes++;
      }
    });

    const isAllCorrect = mistakes === 0;
    const resultMsg = isAllCorrect
      ? `Well done, ${activePlayer}! You counted and colored all the beads correctly!`
      : `You have ${mistakes} mistake${mistakes > 1 ? 's' : ''}, ${activePlayer}. Count and color the beads according to each number.`;

    setGameResult({ ok: isAllCorrect, message: resultMsg });
    setShowModal(true);

    speakText(resultMsg);
  };

  const closeModal = () => {
    setShowModal(false);
    handlePlayAgain();
  };

  const speakInstructionManually = () => {
    speakText(`Hello ${activePlayer}! Click the white beads to color them so the count matches the big number in each circle.`);
  };

  return (
    <div style={styles.container}>
      <main style={styles.main}>
        {/* Controls Toolbar */}
        <section style={styles.toolbar}>
          {/* Player Input */}
          <div style={styles.inputGroup}>
            <User style={{ width: 18, height: 18, color: '#3B82F6' }} />
            <input
              type="text"
              placeholder="Enter Name"
              value={playerName}
              onChange={handleNameInputChange}
              style={styles.input}
            />
          </div>

          {/* Voice Audio Controls Group */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={speakInstructionManually}
              style={{
                ...styles.voiceBtn,
                opacity: isMuted ? 0.6 : 1
              }}
              title="Listen to Instructions"
            >
              <Volume2 style={{ width: 18, height: 18 }} /> Listen
            </motion.button>

            {/* Mute / Unmute Toggle */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleMute}
              style={{
                ...styles.muteBtn,
                background: isMuted ? '#FEE2E2' : '#F1F5F9',
                color: isMuted ? '#EF4444' : '#64748B'
              }}
              title={isMuted ? "Unmute Voice" : "Mute Voice"}
            >
              {isMuted ? <VolumeX style={{ width: 18, height: 18 }} /> : <Volume2 style={{ width: 18, height: 18 }} />}
            </motion.button>
          </div>

          {/* Play Again Button */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handlePlayAgain}
            style={styles.playBtn}
          >
            <Play style={{ width: 16, height: 16 }} /> Play Again
          </motion.button>

          {/* Timer Display */}
          <div style={styles.timerGroup}>
            <label style={styles.timerLabel}>
              <input
                type="checkbox"
                checked={isTimerActive}
                onChange={(e) => setIsTimerActive(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Timer
            </label>
            <div style={styles.timerText}>
              <Clock style={{ width: 16, height: 16 }} />
              <span>{timer}s</span>
            </div>
          </div>
        </section>

        {/* Main Game Stage */}
        <section style={styles.gameCardWhite}>
          <div style={styles.gameHeader}>
            <h2 style={styles.gameTitle}>
              <Sparkles style={{ width: 22, height: 22, color: '#F59E0B' }} /> Count and Colour the Beads
            </h2>
            <span style={styles.playerBadge}>
              Player: {activePlayer}
            </span>
          </div>

          {/* Circular Bead Stage */}
          <div style={styles.circlesGrid}>
            {initialBeadCirclesData.map((circle) => {
              const circleBeads = selectedBeads[circle.id] || [];

              return (
                <div
                  key={circle.id}
                  style={{
                    ...styles.circleContainer,
                    backgroundColor: circle.color
                  }}
                >
                  {/* Central Target Number */}
                  <div style={styles.numberLabel}>
                    {circle.num}
                  </div>

                  {/* Render 10 beads circularly around circle boundary */}
                  {Array.from({ length: TOTAL_BEADS_PER_CIRCLE }).map((_, bIndex) => {
                    const deg = bIndex * (360 / TOTAL_BEADS_PER_CIRCLE);
                    const rad = (deg * Math.PI) / 180;
                    // Positioning around 180px container radius
                    const leftPos = 75 + 65 * Math.cos(rad);
                    const topPos = 75 + 65 * Math.sin(rad);
                    const isColored = circleBeads.includes(bIndex);

                    return (
                      <motion.div
                        key={bIndex}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.85 }}
                        onClick={() => handleBeadClick(circle.id, bIndex)}
                        style={{
                          ...styles.bead,
                          left: `${leftPos}px`,
                          top: `${topPos}px`,
                          backgroundColor: isColored ? BEAD_COLOR : '#FFFFFF',
                          borderColor: isColored ? '#2563EB' : '#FFFFFF'
                        }}
                        title={`Bead ${bIndex + 1}`}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Action Bar */}
          <div style={styles.actionBar}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSubmit}
              style={styles.submitBtn}
            >
              Submit Answer
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleResetCurrent}
              style={styles.resetBtn}
            >
              <RefreshCw style={{ width: 18, height: 18 }} /> Reset Choices
            </motion.button>
          </div>
        </section>
      </main>

      {/* Modal / Popup Overlay */}
      <AnimatePresence>
        {showModal && (
          <div style={styles.modalOverlay}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              style={styles.modalCard}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                {gameResult.ok ? (
                  <div style={styles.iconCircleSuccess}>
                    <Trophy style={{ width: 44, height: 44, color: '#10B981' }} />
                  </div>
                ) : (
                  <div style={styles.iconCircleFail}>
                    <XCircle style={{ width: 44, height: 44, color: '#EF4444' }} />
                  </div>
                )}
              </div>

              {gameResult.ok && (
                <div style={{ color: '#F59E0B', fontSize: '1.4rem', marginBottom: 8, letterSpacing: '2px' }}>
                  ⭐⭐⭐⭐⭐
                </div>
              )}

              <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1E293B', marginBottom: 8 }}>
                {gameResult.ok ? `Great job, ${activePlayer}!` : `Try Again, ${activePlayer}!`}
              </h3>

              <p style={{ color: '#64748B', fontSize: '0.95rem', marginBottom: 16, lineHeight: 1.4 }}>
                {gameResult.message}
              </p>

              <div style={styles.modalTimerBox}>
                <Clock style={{ width: 16, height: 16, color: '#2563EB' }} /> Time Spent: <strong>{timer}s</strong>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={closeModal}
                style={styles.modalCloseBtn}
              >
                Play Again
              </motion.button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#F1F5F9',
    color: '#0F172A',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    padding: '32px 16px',
    boxSizing: 'border-box'
  },
  main: {
    maxWidth: '900px',
    margin: '0 auto'
  },
  toolbar: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '16px',
    padding: '12px 20px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#F8FAFC',
    borderRadius: '10px',
    padding: '8px 12px',
    border: '1px solid #CBD5E1'
  },
  input: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#0F172A',
    fontSize: '0.9rem',
    fontWeight: '500',
    width: '120px'
  },
  voiceBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#F1F5F9',
    border: '1px solid #CBD5E1',
    color: '#2563EB',
    padding: '8px 14px',
    borderRadius: '10px',
    fontWeight: '600',
    fontSize: '0.85rem',
    cursor: 'pointer'
  },
  muteBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 10px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  playBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#2563EB',
    border: 'none',
    color: '#FFFFFF',
    padding: '10px 18px',
    borderRadius: '10px',
    fontWeight: '600',
    fontSize: '0.9rem',
    cursor: 'pointer'
  },
  timerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    background: '#F8FAFC',
    borderRadius: '10px',
    padding: '8px 14px',
    border: '1px solid #CBD5E1'
  },
  timerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#475569',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer'
  },
  timerText: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#2563EB',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    fontSize: '0.95rem'
  },
  gameCardWhite: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '20px',
    padding: '28px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)'
  },
  gameHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '2px solid #F1F5F9',
    paddingBottom: '16px',
    marginBottom: '20px'
  },
  gameTitle: {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#0F172A',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  playerBadge: {
    fontSize: '0.8rem',
    fontWeight: '600',
    background: '#EFF6FF',
    color: '#2563EB',
    border: '1px solid #BFDBFE',
    padding: '4px 12px',
    borderRadius: '20px'
  },
  circlesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '24px',
    padding: '12px 0'
  },
  circleContainer: {
    position: 'relative',
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
  },
  numberLabel: {
    fontSize: '3.5rem',
    fontWeight: '800',
    color: '#FFFFFF',
    userSelect: 'none',
    textShadow: '0 2px 4px rgba(0,0,0,0.15)'
  },
  bead: {
    position: 'absolute',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: '2px solid #FFFFFF',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
    transition: 'background-color 0.2s ease, border-color 0.2s ease'
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '2px solid #F1F5F9'
  },
  submitBtn: {
    padding: '12px 28px',
    background: '#059669',
    border: 'none',
    borderRadius: '12px',
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: '1rem',
    cursor: 'pointer'
  },
  resetBtn: {
    padding: '12px 22px',
    background: '#F1F5F9',
    border: '1px solid #CBD5E1',
    borderRadius: '12px',
    color: '#475569',
    fontWeight: '600',
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(4px)'
  },
  modalCard: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '20px',
    padding: '28px',
    maxWidth: '360px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
  },
  iconCircleSuccess: {
    padding: '12px',
    background: '#ECFDF5',
    border: '1px solid #A7F3D0',
    borderRadius: '50%'
  },
  iconCircleFail: {
    padding: '12px',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '50%'
  },
  modalTimerBox: {
    background: '#F8FAFC',
    borderRadius: '8px',
    padding: '8px 16px',
    marginBottom: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#334155',
    border: '1px solid #E2E8F0',
    fontSize: '0.9rem'
  },
  modalCloseBtn: {
    width: '100%',
    padding: '12px',
    background: '#2563EB',
    border: 'none',
    color: '#FFFFFF',
    fontWeight: '700',
    borderRadius: '10px',
    cursor: 'pointer'
  }
};